import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, ChevronRight, Sparkles, Menu } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../LanguageSelector';

export const HamburgerMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { t } = useTranslation();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Function to calculate and set the scale
  const calculateScale = () => {
    if (!menuRef.current) return;
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = 650; // Our base design width
    
    // Calculate scale based on viewport width with some padding
    const widthScale = (viewportWidth - 80) / menuWidth; // 40px padding on each side
    const heightScale = viewportHeight / menuRef.current.scrollHeight;
    
    // Use the smaller scale to ensure menu fits both width and height
    const newScale = Math.min(widthScale, heightScale, 1);
    setScale(Math.max(newScale, 0.65)); // Don't go smaller than 65%
  };

  // Calculate scale on mount and window resize
  useEffect(() => {
    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  // Add this function to calculate image scaling
  const calculateImageScale = () => {
    const containerAspectRatio = window.innerWidth / window.innerHeight;
    const baseScale = 1/scale + 0.1; // Base inverse scale with padding
    
    // Add extra scaling based on aspect ratio differences
    const extraScale = containerAspectRatio > 1 ? 1.2 : 1.4; // More scaling for portrait mode
    
    return baseScale * extraScale;
  };

  // Preload images when menu opens
  useEffect(() => {
    if (isOpen && !imagesPreloaded) {
      const imagesToPreload = [
        '/assets/images/Mary.jpg',
        '/assets/images/Pachanga.webp',
        '/assets/images/Ellery.webp'
      ];

      Promise.all(
        imagesToPreload.map(src => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = src;
            img.onload = resolve;
            img.onerror = reject;
          });
        })
      ).then(() => setImagesPreloaded(true));
    }
  }, [isOpen, imagesPreloaded]);

  // Menu content for right panel based on hovered item
  const menuContent = {
    packages: {
      smallTitle: "Trending",
      title: "Pachanga at Jacy'z",
      description: "Pachanga is trending on TikTok. Experience the most talked about spa experience in Stockholm.",
      image: "/assets/images/Pachanga.webp"
    },
    meetings: {
      smallTitle: "Stockholm based",
      title: "Ellery Beach House",
      description: "Where luxury meets the Stockholm archipelago. A perfect blend of sophistication and coastal charm.",
      image: "/assets/images/Ellery.webp"
    },
    default: {
      smallTitle: "Featured Destination",
      title: "Maryhill Estate",
      description: "Experience luxury reimagined in the heart of Stockholm. Where modern elegance meets timeless sophistication.",
      image: "/assets/images/Mary.jpg"
    }
  };

  // Menu items with title case instead of all caps
  const menuItems = [
    { id: 'about', title: 'About Us', path: '/about' },
    { id: 'gallery', title: 'Gallery', path: '/gallery' },
    { divider: true },
    { id: 'packages', title: 'Packages & Offers', path: '/packages' },
    { id: 'meetings', title: 'Meeting & Events', path: '/meetings' },
    { id: 'dining', title: 'Dining & Drinks', path: '/dining' },
    { id: 'rooms', title: 'Rooms & Suites', path: '/rooms' },
    { id: 'pool', title: 'Pool Club & Spa', path: '/pool' },
    { divider: true },
    { id: 'entertainment', title: 'Entertainment', path: '/entertainment' },
    { 
      id: 'things', 
      title: 'Things To Do', 
      path: '/things-to-do',
      hasChildren: true 
    },
    { id: 'parking', title: 'Parking', path: '/parking' },
  ];

  const closeMenu = () => {
    setIsOpen(false);
    setExpandedSection(null);
    setHoveredItem(null);
  };

  // Get current content based on hovered item or default
  const currentContent = menuContent[hoveredItem as keyof typeof menuContent] || menuContent.default;

  return (
    <>
      {/* Menu Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 hover:bg-gray-100 rounded-lg"
        aria-label="Open menu"
      >
        <Menu className="h-6 w-6 text-gray-600" />
      </button>

      {/* Overlay - only visible when menu is open */}
      <div
        className={`absolute inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Menu Panel - with continuous scaling and toggle behavior */}
      <div
        ref={menuRef}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'left top',
          width: '650px',
          minHeight: '100%',
          visibility: isOpen ? 'visible' : 'hidden',
        }}
        className={`absolute top-0 left-0 z-50 bg-white transition-all duration-300 ease-in-out ${
          isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
        }`}
      >
        <div className="flex flex-col overflow-hidden">
          <div className="px-[60px] pt-[56px] pb-[32px] flex items-center justify-between">
            <h2 className="text-[22px] font-semibold text-gray-900">{t('mainMenu.title')}</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-[28px] w-[28px] text-gray-600" />
            </button>
          </div>
          
          {/* Main menu - fixed spacing */}
          <div className="px-[60px] mt-[24px] flex flex-col flex-1">
            <nav className="flex-grow">
              {menuItems.map((item, index) => (
                <React.Fragment key={item.id || index}>
                  {item.divider ? (
                    <div className="border-t border-gray-200 my-[24px]"></div>
                  ) : (
                    <div className="mb-[24px]">
                      <div className="flex items-center justify-between">
                        <Link
                          to={item.path}
                          className={`text-[18px] ${
                            location.pathname === item.path || hoveredItem === item.id
                              ? 'text-[#a51c30] font-bold' 
                              : 'text-[#2d2f4e] font-medium'
                          } hover:text-[#a51c30] transition-colors duration-300`}
                          onClick={closeMenu}
                          onMouseEnter={() => setHoveredItem(item.id)}
                          onMouseLeave={() => setHoveredItem(null)}
                        >
                          {item.title}
                        </Link>
                        {item.hasChildren && (
                          <ChevronDown className="w-[20px] h-[20px] text-[#2d2f4e]" />
                        )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
              
              {/* Horizontal utility links with separator */}
              <div className="border-t border-gray-200 my-[32px]"></div>
              <div className="flex flex-wrap gap-[20px] text-[15px] text-[#2d2f4e]">
                <Link 
                  to="/gift-vouchers" 
                  className="hover:text-[#a51c30] font-medium transition-colors duration-300"
                  onClick={closeMenu}
                >
                  Gift Vouchers
                </Link>
                <Link 
                  to="/find-us" 
                  className="hover:text-[#a51c30] font-medium transition-colors duration-300"
                  onClick={closeMenu}
                >
                  Find Us
                </Link>
                <Link 
                  to="/contact" 
                  className="hover:text-[#a51c30] font-medium transition-colors duration-300"
                  onClick={closeMenu}
                >
                  Contact Us
                </Link>
              </div>
              
              {/* Box container with video and text */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8 mt-16">
                <div className="relative h-64">
                  <video 
                    src="/assets/images/essoess.mp4" 
                    poster="/assets/images/rox.webp"
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-[#2d2f4e] mb-2">ESSOESS</h3>
                  <p className="text-xs text-gray-600 mb-3">
                    {t('promotions.essoess')}
                  </p>
                  <Link 
                    to="/venue/essoess" 
                    className="text-xs text-[#a51c30] font-medium flex items-center"
                    onClick={closeMenu}
                  >
                    {t('promotions.learnMore')}
                    <ChevronRight className="w-3 h-3 ml-1" />
                  </Link>
                </div>
              </div>

              {/* Promo block (only visible on mobile) */}
              <div className="px-6 py-4 md:hidden mt-auto">
                <div className="relative h-40">
                  <img 
                    src="/assets/images/rox.webp" 
                    alt="Rox Resort"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </nav>
          </div>

          <div className="border-t border-gray-200 px-6 py-6 sm:px-8">
            <div className="flex items-center justify-between">
              <LanguageSelector />
            </div>
          </div>
        </div>
      </div>

      {/* Right side image section */}
      <div
        className={`fixed inset-y-0 right-0 transition-all duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          left: `${650 * scale}px`,
          zIndex: 45
        }}
      >
        <div className="relative w-full h-full">
          <div className="absolute inset-0 bg-[#2d2f4e]" />
          
          <div className="absolute inset-0 overflow-hidden">
            {/* Background images with crossfade */}
            {Object.entries(menuContent).map(([key, content]) => (
              <div 
                key={key}
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${content.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'left center',
                  opacity: (hoveredItem === key || (!hoveredItem && key === 'default')) ? 1 : 0,
                  transition: 'opacity 0.6s ease-in-out',
                  zIndex: (hoveredItem === key || (!hoveredItem && key === 'default')) ? 2 : 1
                }}
              />
            ))}
          </div>

          {/* Gradient overlay */}
          <div 
            className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"
            style={{
              zIndex: 3
            }}
          />

          {/* Content */}
          <div 
            className="absolute inset-0 flex flex-col justify-end p-16"
            style={{ zIndex: 4 }}
          >
            <div 
              className="text-left"
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'left bottom',
                position: 'absolute',
                bottom: '4rem',
                left: '4rem',
                width: 'max-content',
                maxWidth: '600px',
                transition: 'opacity 0.4s ease-in-out'
              }}
            >
              <span className="inline-block text-sm uppercase tracking-wider mb-2 text-[#fabddf]">
                {currentContent.smallTitle}
              </span>
              <h2 className="text-5xl font-semibold text-white mb-4">
                {currentContent.title}
              </h2>
              <p className="text-lg font-light text-gray-200">
                {currentContent.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}; 